"use client";

import { ArrowRight, ChevronDown, X } from "lucide-react";
import { type FormEvent, useState } from "react";

const businessTypes = ["Retail", "Restaurant"];

export function ProductDemoForm({
  productName,
  onClose,
}: {
  productName: string;
  onClose?: () => void;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [hasError, setHasError] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setResponseMessage("");
    setHasError(false);

    const form = event.currentTarget;
    const formData = new FormData(form);
    const businessType = String(formData.get("businessType") ?? "");
    const city = String(formData.get("city") ?? "");
    const data: Record<string, string> = {
      firstName: String(formData.get("name") ?? ""),
      phone: `+91${String(formData.get("phone") ?? "")}`,
      status: "Existing",
      softwareRequired: `Reecost ${productName} - ${businessType}`,
      message: `Business type: ${businessType}. City: ${city}.`,
    };

    try {
      if (typeof grecaptcha === "undefined") {
        throw new Error(
          "Demo form verification is unavailable. Please try again later.",
        );
      }

      const recaptchaSiteKey = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY;

      if (!recaptchaSiteKey) {
        throw new Error(
          "Demo form verification is unavailable. Please try again later.",
        );
      }

      await new Promise<void>((resolve) => grecaptcha.ready(resolve));
      const recaptchaToken = await grecaptcha.execute(recaptchaSiteKey, {
        action: "submit",
      });

      const captchaResponse = await fetch("/api/captcha", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recaptchaToken }),
      });
      const captchaResult = await captchaResponse.json();

      if (!captchaResponse.ok) {
        throw new Error(
          captchaResult.message ?? "Verification failed. Please try again.",
        );
      }

      const contactResponse = await fetch("/api/zoho", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, recaptchaToken }),
      });
      const contactResult = await contactResponse.json();

      if (!contactResponse.ok) {
        throw new Error(
          contactResult.message ??
            "We couldn’t send your request. Please try again.",
        );
      }

      setResponseMessage(
        `Thanks! Our ${productName.toLowerCase()} expert will be in touch soon.`,
      );
      form.reset();
    } catch (error) {
      console.error(`${productName} demo request failed:`, error);
      setHasError(true);
      setResponseMessage(
        error instanceof Error
          ? error.message
          : "We couldn’t send your request. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="relative min-w-0 rounded-[20px] bg-white p-6 shadow-[0_24px_30px_rgba(20,5,70,0.35)] sm:p-7"
    >
      {onClose ? (
        <button
          aria-label="Close"
          className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full border border-[#e7defb] bg-transparent text-[#5b5b6b] transition hover:bg-[#f3eefe]"
          onClick={onClose}
          type="button"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      ) : null}
      <h3 className="pr-10 font-semibold text-[#0f0f1a] text-xl">
        Book your free demo
      </h3>
      <p className="mt-1 text-[#5b5b6b] text-sm">Takes less than a minute.</p>

      <div className="mt-4 grid grid-cols-[minmax(0,1fr)] gap-3">
        <input
          aria-label="Your name"
          autoComplete="name"
          className="h-[52px] w-full rounded-[10px] border border-[#e7defb] bg-[#faf8ff] px-4 text-[#0f0f1a] text-sm placeholder:text-[#8a8a99] focus-visible:ring-[#7c3aed]"
          name="name"
          placeholder="Your name"
          required
        />
        <div className="flex h-[52px] items-center rounded-[10px] border border-[#e7defb] bg-[#faf8ff] px-4">
          <span className="border-[#e7defb] border-r pr-3 font-semibold text-[#5b5b6b] text-sm">
            +91
          </span>
          <input
            aria-label="Mobile number"
            autoComplete="tel-national"
            className="h-full min-w-0 flex-1 border-0 bg-transparent pl-3 text-[#0f0f1a] text-sm placeholder:text-[#8a8a99] focus-visible:ring-0"
            inputMode="numeric"
            maxLength={10}
            name="phone"
            pattern="[0-9]{10}"
            placeholder="Mobile number"
            required
            title="Enter a 10-digit mobile number"
            type="tel"
          />
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-2">
          <div className="relative">
            <select
              aria-label="Business type"
              className="h-[52px] w-full appearance-none rounded-[10px] border border-[#e7defb] bg-[#faf8ff] px-4 pr-10 text-[#0f0f1a] text-sm focus-visible:ring-[#7c3aed]"
              defaultValue=""
              name="businessType"
              required
            >
              <option disabled value="">
                Business type
              </option>
              {businessTypes.map((businessType) => (
                <option key={businessType} value={businessType}>
                  {businessType}
                </option>
              ))}
            </select>
            <ChevronDown
              aria-hidden="true"
              className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-3 h-[18px] w-[18px] text-[#6f6c8f]"
            />
          </div>
          <input
            aria-label="City"
            autoComplete="address-level2"
            className="h-[52px] w-full rounded-[10px] border border-[#e7defb] bg-[#faf8ff] px-4 text-[#0f0f1a] text-sm placeholder:text-[#8a8a99] focus-visible:ring-[#7c3aed]"
            name="city"
            placeholder="City"
            required
          />
        </div>
      </div>

      <button
        className="mt-6 flex h-11 w-full items-center justify-center gap-1 rounded-lg bg-[#7c3aed] px-5 font-semibold text-sm text-white shadow-sm transition hover:bg-[#6d28d9] disabled:cursor-not-allowed disabled:opacity-70"
        disabled={isSubmitting}
        type="submit"
      >
        {isSubmitting ? "Sending..." : "Book Free Demo"}
        <ArrowRight aria-hidden="true" className="h-5 w-5" />
      </button>
      <p className="mt-3 text-center text-[#8a8a99] text-xs">
        Free demo · No obligation · Your details stay private
      </p>
      {responseMessage && (
        <p
          aria-live="polite"
          className={`mt-3 text-center text-sm ${hasError ? "text-red-600" : "text-green-700"}`}
          role={hasError ? "alert" : "status"}
        >
          {responseMessage}
        </p>
      )}
    </form>
  );
}
